/**
 * Line the editor and the PDF preview up by their text, not by how far down
 * each one is: the PDF adds a header, footer and margins on every page that
 * the editor doesn't have, so the same paragraph sits at a different fraction
 * of each. Each editor paragraph is found among the PDF's lines (in order),
 * and positions between two matched paragraphs are interpolated.
 */

export const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * pdf.js text items ({ str, x, top }) on one page → lines, top to bottom.
 * Items whose tops are within `tol` px are one line, read left to right.
 */
export function groupLines(items, tol = 3) {
  const sorted = items.filter((i) => i.str && i.str.trim())
    .sort((a, b) => a.top - b.top || a.x - b.x);
  const lines = [];
  for (const it of sorted) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(it.top - last.top) <= tol) last.items.push(it);
    else lines.push({ top: it.top, items: [it] });
  }
  return lines.map((l) => ({
    top: l.top,
    text: l.items.sort((a, b) => a.x - b.x).map((i) => i.str).join(" "),
  }));
}

/**
 * Drop text that repeats at the same height on more than one page — the
 * header and footer — so a paragraph is never matched to a later page's band.
 * lines: [{ text, page, frac }].
 */
export function dropRepeats(lines) {
  const pagesOf = new Map();
  for (const l of lines) {
    const k = `${norm(l.text)}@${Math.round(l.frac * 50)}`;
    if (!pagesOf.has(k)) pagesOf.set(k, new Set());
    pagesOf.get(k).add(l.page);
  }
  return lines.filter((l) => pagesOf.get(`${norm(l.text)}@${Math.round(l.frac * 50)}`).size < 2);
}

const keyOf = (text) => norm(String(text || "").split("{{")[0]).slice(0, 20);

/**
 * blocks: editor paragraphs [{ text, y }]; lines: PDF lines [{ text, y }],
 * both top to bottom. Returns [[editorY, pdfY], …], increasing on both sides.
 */
export function matchAnchors(blocks, lines, window = 80) {
  const pdf = lines.map((l) => {
    const n = norm(l.text);
    return { y: l.y, n, bare: n.replace(/^\d+/, "") };   // "1. Company" — the editor's list number is CSS
  });
  const pairs = [];
  let from = 0;
  let lastE = -Infinity;
  let lastP = -Infinity;
  for (const b of blocks) {
    const key = keyOf(b.text);
    if (key.length < 6) continue;
    for (let j = from; j < Math.min(pdf.length, from + window); j += 1) {
      const p = pdf[j];
      const hit = [p.n, p.bare].some((n) => n.startsWith(key) || (n.length >= 6 && key.startsWith(n)));
      if (!hit) continue;
      if (b.y > lastE && p.y > lastP) {
        pairs.push([b.y, p.y]);
        lastE = b.y;
        lastP = p.y;
      }
      from = j + 1;
      break;
    }
  }
  return pairs;
}

/** Piecewise-linear lookup of x in [[a, b], …] sorted by a. */
export function interp(pairs, x) {
  if (!pairs.length) return x;
  if (x <= pairs[0][0]) return pairs[0][1] + (x - pairs[0][0]);
  for (let i = 1; i < pairs.length; i += 1) {
    const [a1, b1] = pairs[i];
    if (x <= a1) {
      const [a0, b0] = pairs[i - 1];
      return a1 === a0 ? b1 : b0 + ((x - a0) / (a1 - a0)) * (b1 - b0);
    }
  }
  const [a, b] = pairs[pairs.length - 1];
  return b + (x - a);
}
